import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Lock, Phone, GraduationCap, Briefcase, ArrowRight, CheckCircle, Eye, EyeOff, FileText, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const InscriptionEvaluateur = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [charteAccepted, setCharteAccepted] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    telephone: '',
    diplomes: '',
    experience: '',
  });

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "L'email est requis";
    if (!formData.password) newErrors.password = "Le mot de passe est requis";
    if (formData.password.length < 6) newErrors.password = "Le mot de passe doit faire au moins 6 caracteres";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    if (!formData.nom) newErrors.nom = "Le nom est requis";
    if (!formData.prenom) newErrors.prenom = "Le prenom est requis";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    if (!charteAccepted) {
      toast.error("Vous devez accepter la charte pour continuer");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    try {
      setLoading(true);
      await axios.post(`${API}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        role: 'evaluateur',
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone,
      });
      setSuccess(true);
      toast.success("Inscription enregistree !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center" data-testid="inscription-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Inscription enregistree !</h2>
          <p className="text-slate-600 mb-6">
            Votre demande d'inscription en tant qu'evaluateur a ete envoyee. 
            Le centre de formation validera votre inscription prochainement. 
            Vous recevrez un email de confirmation.
          </p>
          <Link to="/">
            <Button className="bg-[#8DC63F] hover:bg-[#7ab534] text-white">
              Retour a l'accueil
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full" data-testid="inscription-evaluateur-form">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Inscription Evaluateur</h1>
          <p className="text-slate-500 text-sm mt-1">FISL - Formation Motonautisme</p>
          <div className="flex justify-center mt-4 gap-2">
            <div className={`w-8 h-1 rounded ${step >= 1 ? 'bg-[#8DC63F]' : 'bg-slate-200'}`} />
            <div className={`w-8 h-1 rounded ${step >= 2 ? 'bg-[#8DC63F]' : 'bg-slate-200'}`} />
          </div>
          <p className="text-xs text-slate-400 mt-2">Etape {step} / 2</p>
        </div>

        {/* Step 1: Personal info */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Prenom *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    value={formData.prenom}
                    onChange={(e) => handleChange('prenom', e.target.value)}
                    className="pl-9"
                    placeholder="Prenom"
                    data-testid="input-prenom"
                  />
                </div>
                {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
              </div>
              <div>
                <Label className="text-sm">Nom *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    value={formData.nom}
                    onChange={(e) => handleChange('nom', e.target.value)}
                    className="pl-9"
                    placeholder="Nom"
                    data-testid="input-nom"
                  />
                </div>
                {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
              </div>
            </div>

            <div>
              <Label className="text-sm">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="pl-9"
                  placeholder="votre@email.fr"
                  data-testid="input-email"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <Label className="text-sm">Telephone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={formData.telephone}
                  onChange={(e) => handleChange('telephone', e.target.value)}
                  className="pl-9"
                  placeholder="06 xx xx xx xx"
                  data-testid="input-telephone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Mot de passe *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="pl-9 pr-9"
                    placeholder="Min. 6 caracteres"
                    data-testid="input-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5">
                    {showPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>
              <div>
                <Label className="text-sm">Confirmer *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    className="pl-9 pr-9"
                    placeholder="Confirmer"
                    data-testid="input-confirm-password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-2.5">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>
            </div>

            <Button
              onClick={handleNext}
              className="w-full bg-[#8DC63F] hover:bg-[#7ab534] text-white mt-4"
              data-testid="btn-next"
            >
              Suivant <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Step 2: Charte */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-2">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-[#0F172A]" />
                <h3 className="font-semibold text-slate-800">Charte de l'Evaluateur EPMSP</h3>
              </div>
              
              <div className="bg-white border rounded-lg p-4 max-h-64 overflow-y-auto text-sm text-slate-700 space-y-3" data-testid="charte-content">
                <p className="font-medium">
                  Vous etes en situation d'evaluateur sur les tests du BPJEPS Motonautisme et Disciplines Associees.
                  Si vous n'etes pas agents de l'Etat, vous etes considere comme un collaborateur occasionnel du Service Public.
                </p>
                
                <h4 className="font-bold text-slate-800 mt-3">RESPECT DES REGLES DE DEONTOLOGIE</h4>
                
                <div>
                  <h5 className="font-semibold">Equite et Egalite de traitement</h5>
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    <li>Respecter le cadre reglementaire strict tout au long de l'evaluation</li>
                    <li>Veiller a ce qu'aucun element discriminatoire ne perturbe l'evaluation</li>
                    <li>Evaluer les competences uniquement en fonction de la prestation</li>
                    <li>N'intervenir que pour des raisons de securite</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold">Neutralite et Objectivite</h5>
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    <li>Faire abstraction de tout interet personnel et professionnel</li>
                    <li>Ne pas evaluer une personne connue personnellement</li>
                    <li>Adopter une attitude neutre et bienveillante</li>
                    <li>Ne pas porter de jugements de valeur</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold">Confidentialite et Solidarite</h5>
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    <li>Garantir la discretion professionnelle</li>
                    <li>Ne pas rapporter les avis a l'exterieur</li>
                    <li>Garantir la solidarite entre evaluateurs</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold">Accueil du candidat</h5>
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    <li>Inviter le candidat a emarger et presenter sa convocation et une piece d'identite</li>
                    <li>Se presenter en tant qu'evaluateur</li>
                    <li>Presenter les conditions du deroulement de l'epreuve</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold">Formulation des avis</h5>
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    <li>Se concerter pour formuler une proposition d'avis</li>
                    <li>L'avis doit etre argumente et motive</li>
                    <li>L'argumentaire doit etre en coherence avec les competences evaluees</li>
                  </ul>
                </div>
              </div>

              <a
                href={`${API}/api/evaluateur/charte-pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mt-3"
                data-testid="charte-pdf-link"
              >
                <ExternalLink className="h-4 w-4" />
                Telecharger la charte complete (PDF)
              </a>
            </div>

            <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-white">
              <input
                type="checkbox"
                id="charte-accept"
                checked={charteAccepted}
                onChange={(e) => setCharteAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#8DC63F] focus:ring-[#8DC63F]"
                data-testid="charte-checkbox"
              />
              <label htmlFor="charte-accept" className="text-sm text-slate-700 cursor-pointer">
                J'ai lu et j'accepte la charte de l'evaluateur EPMSP. Je m'engage a respecter les regles de deontologie, 
                d'equite, de neutralite et de confidentialite definies dans ce document.
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
                data-testid="btn-back"
              >
                Retour
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !charteAccepted}
                className="flex-1 bg-[#8DC63F] hover:bg-[#7ab534] text-white disabled:opacity-50"
                data-testid="btn-submit"
              >
                {loading ? "Envoi..." : "Valider l'inscription"}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
            Deja inscrit ? Connectez-vous
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InscriptionEvaluateur;
